import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { base44 } from "@/api/base44Client";
import { sendClerkEmail, withCurrentGreeting } from "@/lib/clerkEmail";
import { statementFileUrl } from "@/lib/feeSlipStatements";
import { fetchStoredFile } from "@/lib/storedFile";
import {
  MAX_ATTACHMENT_BYTES,
  VALS_FINANCE_RECIPIENTS,
  afterFinanceEmailed,
  bytesToBase64,
  financeEmailBody,
  financeEmailSubject,
  invoicePagesOnly,
  isCrimBudgetEmail,
} from "@/lib/disbursementEmail";
import { isClaimReceipt, parseClaimReceipt } from "@/lib/claimReceipts";

// The text of a chosen receipt, so it goes into the Claim receipts folder with its claim
// details. A receipt that cannot be read still goes in, without them.
async function receiptFields(file) {
  try {
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) return {};
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const { extractPdfTextWithPdfJs } = await import("@/lib/standaloneServices");
    const text = await extractPdfTextWithPdfJs(dataUrl);
    return isClaimReceipt(text) ? parseClaimReceipt(text) : {};
  } catch {
    return {};
  }
}

const safeFileName = (value, fallback) =>
  (String(value || "").replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim() || fallback).slice(0, 120);

// "Email finance" for one VALS disbursement: who to send to, the wording (editable), the
// provider's invoice (attached from what is stored) and the ATLAS claim receipt.
// Nothing is sent until Send is pressed. `onSent` receives the fields to record on the
// disbursement.
//
// The receipt (Emma, 27 Oct 2026): any receipt already in the Claim receipts folder against
// this disbursement (`folderReceipts`) is attached, ticked, so nothing has to be chosen again.
// A receipt chosen here instead is sent and then kept in the folder against the disbursement
// too (`onReceiptToFolder({ fields, file })`), so every receipt ends up in one place.
export default function FinanceEmailPanel({ item, userName, signOff, buttonClass, onSent, onClose, folderReceipts = [], onReceiptToFolder }) {
  const invoiceUrl = statementFileUrl({ fileUrl: item.fileUrl });
  const [to, setTo] = useState(() => VALS_FINANCE_RECIPIENTS.map((recipient) => recipient.address));
  // Anyone else to copy in (Emma, 26 Oct 2026). Empty unless typed; several separated by commas.
  const [cc, setCc] = useState("");
  const [withInvoice, setWithInvoice] = useState(Boolean(invoiceUrl));
  const [receipt, setReceipt] = useState(null);
  const [folderTicked, setFolderTicked] = useState(() => new Set(folderReceipts.map((entry) => entry.id)));
  const folderChosen = folderReceipts.filter((entry) => folderTicked.has(entry.id));
  const hasReceipt = Boolean(receipt) || folderChosen.length > 0;
  const crim = isCrimBudgetEmail(item);
  const [subject, setSubject] = useState(() => financeEmailSubject(item));
  const [subjectEdited, setSubjectEdited] = useState(false);
  // Until it is changed by hand, the subject follows the invoice (and its "VALS to be
  // reimbursed" tick).
  const shownSubject = subjectEdited ? subject : financeEmailSubject(item);
  const [body, setBody] = useState(() => financeEmailBody(item, { signOff, withInvoice: Boolean(invoiceUrl) }));
  const [bodyEdited, setBodyEdited] = useState(false);
  const [sending, setSending] = useState(false);

  // Until the wording is changed by hand, it follows what is attached.
  const shownBody = useMemo(
    () => (bodyEdited ? body : financeEmailBody(item, { signOff, withInvoice, withReceipt: hasReceipt })),
    [bodyEdited, body, item, signOff, withInvoice, hasReceipt],
  );

  const toggle = (address) =>
    setTo((current) => (current.includes(address) ? current.filter((value) => value !== address) : [...current, address]));

  const send = async () => {
    if (!to.length) return toast.error("Choose who to email.");
    // A crim budget payment has no ATLAS claim yet, so there is no receipt to ask about.
    if (!crim && !hasReceipt && !window.confirm("Send without the ATLAS claim receipt?")) return;
    setSending(true);
    try {
      const attachments = [];
      let total = 0;
      if (withInvoice && invoiceUrl) {
        let bytes = await fetchStoredFile(invoiceUrl);
        if (item.page) {
          // Only this invoice's page(s), not the other clients' invoices in the same PDF.
          const pdfLib = await import("pdf-lib");
          bytes = new Uint8Array(await invoicePagesOnly(bytes, item.page, item.pageCount || 1, pdfLib));
        }
        total += bytes.length;
        attachments.push({
          filename: safeFileName(`${item.supplier || "Invoice"}${item.invoiceRef ? ` ${item.invoiceRef}` : ""}.pdf`, "invoice.pdf"),
          mimeType: "application/pdf",
          data: bytesToBase64(bytes),
        });
      }
      // Receipts already in the Claim receipts folder.
      for (const entry of folderChosen) {
        const url = statementFileUrl({ fileUrl: entry.fileUrl });
        if (!url) continue;
        const bytes = await fetchStoredFile(url);
        total += bytes.length;
        attachments.push({
          filename: safeFileName(entry.fileName || `Claim receipt ${entry.claimId || ""}.pdf`, "claim-receipt.pdf"),
          mimeType: "application/pdf",
          data: bytesToBase64(bytes),
        });
      }
      let stored = folderChosen[0]
        ? { fileName: folderChosen[0].fileName, fileUrl: folderChosen[0].fileUrl, filePath: folderChosen[0].filePath }
        : null;
      let toFolder = null;
      if (receipt) {
        const bytes = new Uint8Array(await receipt.arrayBuffer());
        total += bytes.length;
        if (total > MAX_ATTACHMENT_BYTES) throw new Error("The attachments are too large to email together (3 MB at most). Try a smaller copy of the claim receipt.");
        // Kept on the disbursement first, so the receipt is never sent without a copy here.
        const upload = await base44.integrations.Core.UploadFile({ file: receipt });
        stored = { fileName: receipt.name, fileUrl: upload?.file_url || "", filePath: upload?.storage_path || "" };
        toFolder = { fields: await receiptFields(receipt), file: stored };
        attachments.push({
          filename: safeFileName(receipt.name, "claim-receipt.pdf"),
          mimeType: receipt.type || "application/pdf",
          data: bytesToBase64(bytes),
        });
      }
      if (total > MAX_ATTACHMENT_BYTES) throw new Error("The attachments are too large to email together (3 MB at most).");

      await sendClerkEmail({
        to: to.join(", "),
        cc,
        bcc: "",
        subject: shownSubject,
        body: withCurrentGreeting(shownBody),
        attachments,
      });
      toast.success(`Emailed ${to.join(" and ")}${cc.trim() ? `, cc ${cc.trim()}` : ""}.`);
      onSent(afterFinanceEmailed(item, { to, cc, by: userName, receipt: stored }));
      if (toFolder && onReceiptToFolder) {
        try {
          await onReceiptToFolder(toFolder);
        } catch (error) {
          toast.error(`Sent, but the receipt could not be added to the Claim receipts folder: ${error.message || "try uploading it there."}`);
        }
      }
    } catch (err) {
      toast.error(err.message || "The email could not be sent.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-slate-100 bg-sky-50/40 p-3" aria-label={`Email finance about ${item.clientName}`}>
      <p className="text-xs font-bold text-slate-800">
        {crim ? "Email finance: pay from the crim budget, VALS to be reimbursed" : "Email finance: claimed in ATLAS, ready to pay"}
      </p>
      <div className="flex flex-wrap gap-4">
        {VALS_FINANCE_RECIPIENTS.map((recipient) => (
          <label key={recipient.address} className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={to.includes(recipient.address)} onChange={() => toggle(recipient.address)} className="h-4 w-4" />
            {recipient.label}
          </label>
        ))}
      </div>
      <div>
        <Label htmlFor={`finance-cc-${item.id}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Cc</Label>
        <Input
          id={`finance-cc-${item.id}`}
          value={cc}
          onChange={(event) => setCc(event.target.value)}
          placeholder="Optional - separate several with commas"
          className="mt-1 h-8 text-xs bg-white"
        />
      </div>
      <div>
        <Label htmlFor={`finance-subject-${item.id}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Subject</Label>
        <Input id={`finance-subject-${item.id}`} value={shownSubject} onChange={(event) => { setSubject(event.target.value); setSubjectEdited(true); }} className="mt-1 h-8 text-xs bg-white" />
      </div>
      <div>
        <Label htmlFor={`finance-body-${item.id}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Message</Label>
        <textarea
          id={`finance-body-${item.id}`}
          value={shownBody}
          onChange={(event) => { setBody(event.target.value); setBodyEdited(true); }}
          rows={12}
          className="mt-1 w-full rounded-md border border-slate-200 bg-white p-2 text-xs leading-5"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Attachments</span>
          {invoiceUrl ? (
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={withInvoice} onChange={(event) => setWithInvoice(event.target.checked)} className="h-4 w-4" />
              <Paperclip className="h-3.5 w-3.5" />
              Provider&apos;s invoice{item.page ? ` (page ${item.page}${item.pageCount > 1 ? `-${item.page + item.pageCount - 1}` : ""} only)` : ""}
            </label>
          ) : (
            <p className="text-[11px] text-slate-500">No invoice PDF is stored for this disbursement.</p>
          )}
        </div>
        <div>
          {folderReceipts.map((entry) => (
            <label key={entry.id} className="mb-1 flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={folderTicked.has(entry.id)}
                onChange={() => setFolderTicked((current) => {
                  const next = new Set(current);
                  if (next.has(entry.id)) next.delete(entry.id);
                  else next.add(entry.id);
                  return next;
                })}
                className="h-4 w-4"
                aria-label={`Attach claim receipt ${entry.claimId || entry.fileName}`}
              />
              <Paperclip className="h-3.5 w-3.5" />
              Claim receipt{entry.claimId ? ` ${entry.claimId}` : ""} (from the Claim receipts folder)
            </label>
          ))}
          <Label htmlFor={`finance-receipt-${item.id}`} className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
            {folderReceipts.length ? "Another receipt (optional)" : crim ? "Receipt (optional)" : "ATLAS claim receipt"}
          </Label>
          <Input
            id={`finance-receipt-${item.id}`}
            type="file"
            accept=".pdf,application/pdf,image/*"
            onChange={(event) => setReceipt(event.target.files?.[0] || null)}
            className="mt-1 h-9 text-xs bg-white"
          />
          {item.claimReceiptName && !receipt && !folderReceipts.length && (
            <p className="mt-1 truncate text-[10px] text-slate-500">Last sent: {item.claimReceiptName}</p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" onClick={send} disabled={sending} className={`h-8 gap-1 px-4 text-[11px] text-white ${buttonClass}`}>
          <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={sending} className="h-8 px-3 text-[11px]">Cancel</Button>
      </div>
    </div>
  );
}
